import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import logo from '../../assets/lm-logo-blue.svg';
import { checkForRequiredPermissions } from '../../utils/helpers/permissionsHelpers';
import './styles.scss';

const Header = observer(() => {
  const isLocalEnv = process.env.REACT_APP_LOCAL_ENV === 'true';
  const isBreadcrumbsEnabled = process.env.REACT_APP_BREADCRUMBS === 'true';
  const isSelfServiceEnabled = process.env.REACT_APP_SELF_SERVICE_ENABLED === 'true';
  const isOQPortalEnabled = process.env.REACT_APP_OQ_PORTAL_ENABLED === 'true';
  const isSupervisorPagesEnabled = process.env.REACT_APP_SUPERVISOR_PAGES_ENABLED === 'true';
  const isOnCallPagesEnabled = process.env.REACT_APP_ON_CALL_PAGES_ENABLED === 'true';
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = event => {
      if (isMoreMenuOpen && !event.target.closest('.dropdown-menu')) {
        setIsMoreMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMoreMenuOpen]);

  // Build lookup URL based on environment
  const getLookupUrl = () => {
    if (isLocalEnv) {
      return '/lookup'; // Local dev uses relative path
    }
    // For deployed environments, construct the lookup subdomain URL
    const currentHost = window.location.host;
    const parts = currentHost.split('.');
    // Replace first subdomain with 'lookup'
    parts[0] = 'lookup';
    return `${window.location.protocol}//${parts.join('.')}`;
  };

  const onLogoutButtonClick = () => {
    // Remove specific tokens from localStorage
    localStorage.removeItem('mapServiceAuth');
    localStorage.removeItem(
      `oidc.user:${process.env.REACT_APP_AZURE_AUTH_URL}:${process.env.REACT_APP_AZURE_AUTH_CLIENT_ID}`
    );

    // Redirect to login page
    window.location.href = '/login';
  };

  const hasMoreMenuItems =
    (isBreadcrumbsEnabled && checkForRequiredPermissions('BREADCRUMBS')) ||
    checkForRequiredPermissions('BREADCRUMBS') ||
    (isSelfServiceEnabled && checkForRequiredPermissions('SUPERVISOR_ONCALL')) ||
    checkForRequiredPermissions('SUPERVISOR_ONCALL') ||
    (isOQPortalEnabled && checkForRequiredPermissions('OQ_VIEW'));

  return (
    <header>
      <img className="logo" src={logo} alt="logo" />
      <nav className="app-nav">
        <ul>
          {isSupervisorPagesEnabled && checkForRequiredPermissions('SUPERVISOR') && (
            <>
              <li>
                <NavLink to="/dashboard">Dashboard</NavLink>
              </li>
              <li>
                <NavLink to="/ticketslist" state={{ prevPath: window.location.pathname }}>
                  Tickets List
                </NavLink>
              </li>
            </>
          )}

          {checkForRequiredPermissions('MAPSEARCH') && (
            <li>
              <NavLink end to="/mapsearch">
                Map Search
              </NavLink>
            </li>
          )}

          <li>
            {isLocalEnv ? (
              <NavLink to="/lookup">Lookup</NavLink>
            ) : (
              <a href={getLookupUrl()}>Lookup</a>
            )}
          </li>
          {isSupervisorPagesEnabled && checkForRequiredPermissions('SUPERVISOR') && (
            <li>
              <NavLink to="/archive">Archive</NavLink>
            </li>
          )}

          {isOnCallPagesEnabled && checkForRequiredPermissions('SUPERVISOR_ONCALL') && (
            <li>
              <NavLink to="/notifications">Notifications</NavLink>
            </li>
          )}

          {hasMoreMenuItems && (
            <li
              className="dropdown-menu"
              onClick={e => {
                e.stopPropagation();
                setIsMoreMenuOpen(!isMoreMenuOpen);
              }}
            >
              <span className="dropdown-trigger">More ▾</span>
              {isMoreMenuOpen && (
                <ul className="dropdown-content">
                  {isBreadcrumbsEnabled && checkForRequiredPermissions('BREADCRUMBS') && (
                    <li>
                      <NavLink to="/breadcrumbs">Breadcrumbs</NavLink>
                    </li>
                  )}

                  {checkForRequiredPermissions('BREADCRUMBS') && (
                    <li>
                      <NavLink to="/baseballcards">Baseball Cards</NavLink>
                    </li>
                  )}

                  {isSelfServiceEnabled && checkForRequiredPermissions('SUPERVISOR_ONCALL') && (
                    <li>
                      <NavLink to="/selfservice">Self Service Folders</NavLink>
                    </li>
                  )}

                  {isOQPortalEnabled && checkForRequiredPermissions('OQ_VIEW') && (
                    <li>
                      <NavLink to="/oqportal">OQ Portal</NavLink>
                    </li>
                  )}

                  {checkForRequiredPermissions('SUPERVISOR_ONCALL') && (
                    <li>
                      <NavLink to="/tickettypesummary">Ticket Type Summary</NavLink>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}
        </ul>
      </nav>
      <div className="app-options">
        <div className="session-options">
          <dl>
            <dd>
              <ul>
                <li className="logout">
                  <button onClick={onLogoutButtonClick} className="logout-btn">
                    Logout
                  </button>
                </li>
              </ul>
            </dd>
          </dl>
        </div>
      </div>
    </header>
  );
});

export default Header;
